// Cloudflare Turnstile for the public Riot-proxy pages (scout, draft). The site key comes from /api/config;
// when it is empty (local dev, or not configured) no widget is loaded and requests go without a token.

interface TurnstileApi {
	render(el: HTMLElement, opts: Record<string, unknown>): string;
	remove(id: string): void;
}
declare global {
	interface Window {
		turnstile?: TurnstileApi;
	}
}

let siteKey: Promise<string | null> | undefined;
let script: Promise<void> | undefined;

function getSiteKey(): Promise<string | null> {
	siteKey ??= fetch("/api/config")
		.then(r => r.json())
		.then(d => (d?.data?.turnstileSiteKey as string | null) ?? null)
		.catch(() => null);
	return siteKey;
}

function loadScript(): Promise<void> {
	script ??= new Promise((resolve, reject) => {
		const s = document.createElement("script");
		s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
		s.async = true;
		s.onload = () => resolve();
		s.onerror = () => reject(new Error("Could not load the bot check"));
		document.head.appendChild(s);
	});
	return script;
}

/** A fresh single-use token, or null when Turnstile is not configured. Interactive only if Cloudflare asks. */
export async function getTurnstileToken(): Promise<string | null> {
	const key = await getSiteKey();
	if (!key) return null;
	await loadScript();

	return new Promise((resolve, reject) => {
		const host = document.createElement("div");
		host.style.cssText = "position:fixed;bottom:16px;right:16px;z-index:50";
		document.body.appendChild(host);
		const done = (fn: () => void) => {
			try {
				window.turnstile?.remove(id);
			} catch {
				/* already gone */
			}
			host.remove();
			fn();
		};
		const id = window.turnstile!.render(host, {
			sitekey: key,
			appearance: "interaction-only",
			callback: (token: string) => done(() => resolve(token)),
			"error-callback": () => done(() => reject(new Error("Bot check failed. Reload and try again."))),
			"timeout-callback": () => done(() => reject(new Error("Bot check timed out. Try again.")))
		});
	});
}

/** Headers carrying a Turnstile token (empty when not configured). */
export async function turnstileHeaders(): Promise<Record<string, string>> {
	const token = await getTurnstileToken();
	return token ? { "X-Turnstile-Token": token } : {};
}
